/**
 * Loading Handlers
 * Provides handlers for tracking loading states of API requests
 */

import {LoadingStateChangeHandler} from '../types';

/**
 * Loading state manager interface
 */
export interface LoadingStateManager {
  // Set loading state for a specific key
  setLoading: (key: string, isLoading: boolean) => void;
  // Get loading state for a specific key
  isLoading: (key: string) => boolean;
  // Get global loading state (true if any request is loading)
  isAnyLoading: () => boolean;
  // Get the count of currently loading requests
  getLoadingCount: () => number;
}

/**
 * Creates a loading state manager
 * @returns A loading state manager instance
 */
export const createLoadingStateManager = (): LoadingStateManager => {
  // Map to track loading states by key
  const loadingStates = new Map<string, boolean>();
  // Set to track active request IDs
  const activeRequests = new Set<string>();

  return {
    setLoading: (key: string, isLoading: boolean) => {
      if (isLoading) {
        loadingStates.set(key, true);
      } else {
        loadingStates.delete(key);
      }
    },
    isLoading: (key: string) => {
      return loadingStates.has(key);
    },
    isAnyLoading: () => {
      return loadingStates.size > 0;
    },
    getLoadingCount: () => {
      return loadingStates.size;
    },
  };
};

/**
 * Creates a loading state change handler for a specific key
 * @param loadingStateManager - The loading state manager
 * @param key - The key to track loading state for
 * @returns A loading state change handler
 */
export const createKeyedLoadingStateHandler = (
  loadingStateManager: LoadingStateManager,
  key: string
): LoadingStateChangeHandler => {
  return (isLoading) => {
    loadingStateManager.setLoading(key, isLoading);
  };
};

/**
 * Creates a global loading state change handler
 * @param loadingStateManager - The loading state manager
 * @returns A loading state change handler
 */
export const createGlobalLoadingStateHandler = (
  loadingStateManager: LoadingStateManager
): LoadingStateChangeHandler => {
  // Map to track request-specific loading states
  const requestLoadingStates = new Map<string, boolean>();

  return (isLoading, requestId) => {
    if (!requestId) {
      return;
    }

    if (isLoading) {
      requestLoadingStates.set(requestId, true);
    } else {
      requestLoadingStates.delete(requestId);
    }

    // Update the global loading state based on whether any requests are loading
    loadingStateManager.setLoading('global', requestLoadingStates.size > 0);
  };
};

/**
 * React hook for using the loading state manager
 * This is just a type definition - the actual implementation would be in a separate React hooks file
 */
export type UseLoadingState = (key?: string) => {
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
};
